import React, { useCallback, useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Calendar } from 'lucide-react'
import { Card, CardBody } from '@/components/ui/Card'
import { meetingService } from '@/services/meetingService'
import { useToast } from '@/contexts/ToastContext'
import { Meeting } from '@/types/meeting'

const MEETING_STATUS_LABELS: Record<string, string> = {
  scheduled: 'Confirmed',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  rescheduled: 'Rescheduled'
}

const MEETING_STATUS_STYLES: Record<string, string> = {
  scheduled: 'bg-green-100 text-green-700',
  in_progress: 'bg-accent-100 text-accent-700',
  completed: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-red-100 text-red-700',
  rescheduled: 'bg-yellow-100 text-yellow-700'
}

const formatDateTime = (value: string): string =>
  new Date(value).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })

const MeetingSeriesView: React.FC = () => {
  const { meetingId } = useParams<{ meetingId: string }>()
  const { showToast } = useToast()

  const [series, setSeries] = useState<Meeting[]>([])
  const [loading, setLoading] = useState(true)

  const fetchSeries = useCallback(async () => {
    if (!meetingId) return
    try {
      setLoading(true)
      const data = await meetingService.getMeetingSeries(meetingId)
      setSeries(data)
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to load meeting series'
      })
    } finally {
      setLoading(false)
    }
  }, [meetingId, showToast])

  useEffect(() => {
    fetchSeries()
  }, [fetchSeries])

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <Link
          to={meetingId ? `/meetings/${meetingId}` : '/meetings'}
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to meeting
        </Link>

        <h1 className="text-2xl font-bold text-gray-900 mb-1">Recurring Series</h1>
        <p className="text-gray-600 mb-6">All instances of this recurring meeting</p>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : series.length === 0 ? (
          <p className="text-gray-600">No meetings found in this series.</p>
        ) : (
          series.map(meeting => (
            <Link key={meeting.id} to={`/meetings/${meeting.id}`} className="block">
              <Card className="mb-3 hover:border-primary-300 transition-colors">
                <CardBody>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-sm text-gray-700">
                      <Calendar className="w-4 h-4" />
                      {formatDateTime(meeting.scheduledStartTime)}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        MEETING_STATUS_STYLES[meeting.status] || 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {MEETING_STATUS_LABELS[meeting.status] || meeting.status}
                    </span>
                  </div>
                </CardBody>
              </Card>
            </Link>
          ))
        )}
      </div>
    </div>
  )
}

export default MeetingSeriesView
